import { useQuery } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue } from 'jotai';

import type { EnhanceProjectListingSuggestion } from '@/api/v4/project-enhance-listing-suggestions.api';
import { fetchProjectListingsCount } from '@/api/v4/project-listings.api';
import type { ProjectCompaniesSort } from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { useProjectListings } from '@/features/projects/project-listings/use-project-listings.query';
import { getTimeInMs } from '@/helpers/get-time-in-ms';
import { modalState } from '@/state/modal.state';
import type { IOption } from '@/types';
import type { IPageState, ISort } from '@/ui/table/table.types';

import { projectIdState } from '../../projects/project.state';

export type WizardStep =
  | 'problemDefinition'
  | 'companySelection'
  | 'analyzing'
  | 'results'
  | 'error';

const pageStepState = atom<WizardStep>('problemDefinition');

const problemStatementState = atom<string>('');

const desiredSolutionState = atom<string>('');

const locationsState = atom<IOption[]>([]);

const DEFAULT_PAGE_SIZE = 20;

const companyFilterPageState = atom<IPageState>({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
});

const companyFilterSortState = atom<ISort<ProjectCompaniesSort>>({
  direction: 'desc',
  sort: 'status',
});

const companyFilterSelectedListState = atom<string[]>([]);

const isAllCompaniesSelectedState = atom<boolean>(true);

const addedSuggestionsState = atom<EnhanceProjectListingSuggestion[]>([]);

const rejectedSuggestionsState = atom<EnhanceProjectListingSuggestion[]>([]);

const inProgressSuggestionsState = atom<EnhanceProjectListingSuggestion[]>([]);

const enhanceResultsState = atom<EnhanceProjectListingSuggestion[]>([]);
const enhanceRequestIdState = atom<string | null>(null);

const companySearchState = atom<string>('');

export const useProjectListingSuggestionState = () => {
  const [modal, setModalState] = useAtom(modalState);

  const isModalMounted = modal?.state === 'enhanceProjectForm';

  const [currentPageStep, setCurrentPageStep] = useAtom(pageStepState);

  const projectId = useAtomValue(projectIdState);

  const [companyFilterSort, setCompanyFilterSort] = useAtom(
    companyFilterSortState,
  );

  const [companySearch, setCompanySearch] = useAtom(companySearchState);

  const [companyFilterPage, setCompanyFilterPage] = useAtom(
    companyFilterPageState,
  );

  const { fetchNextPage, hasNextPage, isFetchingNextPage, projectListings } =
    useProjectListings({
      enabled: isModalMounted,
      pageSize: companyFilterPage.pageSize,
      projectId,
      search: companySearch,
      sort: companyFilterSort,
    });

  const { data: listingsCountData } = useQuery({
    enabled: isModalMounted && Boolean(projectId),
    queryFn: () =>
      projectId ? fetchProjectListingsCount(projectId) : undefined,
    queryKey: [QueryKey.ProjectListingsCount, projectId, 'enhance-modal'],
    staleTime: getTimeInMs(5, 'minute'),
  });

  const totalListingsCount = listingsCountData?.total ?? 0;

  const [companyFilterSelected, setCompanyFilterSelected] = useAtom(
    companyFilterSelectedListState,
  );

  const [isAllCompaniesSelected, setIsAllCompaniesSelected] = useAtom(
    isAllCompaniesSelectedState,
  );

  const [problemStatement, setProblemStatement] = useAtom(
    problemStatementState,
  );

  const [desiredSolution, setDesiredSolution] = useAtom(desiredSolutionState);

  const [locations, setLocations] = useAtom(locationsState);

  const [addedSuggestions, setAddedSuggestions] = useAtom(
    addedSuggestionsState,
  );

  const [rejectedSuggestions, setRejectedSuggestions] = useAtom(
    rejectedSuggestionsState,
  );

  const [inProgressSuggestions, setInProgressSuggestions] = useAtom(
    inProgressSuggestionsState,
  );

  const [enhanceResults, setEnhanceResults] = useAtom(enhanceResultsState);
  const [enhanceRequestId, setEnhanceRequestId] = useAtom(
    enhanceRequestIdState,
  );

  const addSuggestionToInProgress = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    setInProgressSuggestions(arr => [...arr, suggestion]);
  };

  const addSuggestionToAdded = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    setAddedSuggestions(arr => [...arr, suggestion]);
  };

  const addSuggestionToRejected = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    setRejectedSuggestions(arr => [...arr, suggestion]);
  };

  const removeSuggestionFromInProgress = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    setInProgressSuggestions(arr =>
      arr.filter(s => s.permalink !== suggestion.permalink),
    );
  };

  const removeSuggestionFromRejected = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    setRejectedSuggestions(arr =>
      arr.filter(s => s.permalink !== suggestion.permalink),
    );
  };

  const setCompanyFilterSelectAll = (selected: boolean) => {
    setIsAllCompaniesSelected(selected);
    setCompanyFilterSelected(
      projectListings && selected
        ? projectListings.map(listing => listing.organization.id)
        : [],
    );
  };

  const toggleCompanyFilterSelected = (organizationId: string) => {
    setIsAllCompaniesSelected(false);
    setCompanyFilterSelected((old: string[]) => {
      if (old.includes(organizationId)) {
        return old.filter(item => item !== organizationId);
      }
      return [...old, organizationId];
    });
  };

  const isSuggestionAdded = (suggestion: EnhanceProjectListingSuggestion) => {
    return !!addedSuggestions.find(s => s.permalink === suggestion.permalink);
  };

  const isSuggestionInProgress = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    return !!inProgressSuggestions.find(
      s => s.permalink === suggestion.permalink,
    );
  };

  const isSuggestionRejected = (
    suggestion: EnhanceProjectListingSuggestion,
  ) => {
    return !!rejectedSuggestions.find(
      s => s.permalink === suggestion.permalink,
    );
  };

  const closeModal = () => {
    setModalState(null);
  };

  return {
    addSuggestionToAdded,
    addSuggestionToInProgress,
    addSuggestionToRejected,
    closeModal,
    companyFilterPage,
    companyFilterSelected,
    companyFilterSortState,
    companySearch,
    currentPageStep,
    desiredSolution,
    enhanceRequestId,
    enhanceResults,
    fetchNextPage,
    hasNextPage,
    inProgressSuggestions,
    isAllCompaniesSelected,
    isFetchingNextPage,
    isSuggestionAdded,
    isSuggestionInProgress,
    isSuggestionRejected,
    locations,
    problemStatement,
    projectId,
    projectListings,
    removeSuggestionFromInProgress,
    removeSuggestionFromRejected,
    setCompanyFilterPage,
    setCompanyFilterSelectAll,
    setCompanyFilterSelected,
    setCompanyFilterSort,
    setCompanySearch,
    setCurrentPageStep,
    setDesiredSolution,
    setEnhanceRequestId,
    setEnhanceResults,
    setLocations,
    setProblemStatement,
    toggleCompanyFilterSelected,
    totalListingsCount,
  };
};
